import { FC } from 'react';
import { useWindowSize } from 'react-use';
import styled from 'styled-components';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Text from 'quarks/Text';

import TabbedSubMenu from 'components/Header/Desktop/TabbedSubMenu';
import SubMenuGroup from 'components/Header/SubMenuGroup';
import useMenuData from 'components/Header/useMenuData';

import parseUrl from 'utils/parseUrl';

import type { ContentfulElementMenu } from 'graphqlTypes';

type MenuItemProps = {
  menu?: ContentfulElementMenu;
  isOpen?: boolean;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  setOpenMenu: () => void;
  handleMobileMenu: () => void;
};

const MenuItem: FC<MenuItemProps> = ({
  menu,
  isOpen,
  setOpenMenu,
  onClick,
  onMouseEnter,
  handleMobileMenu,
  ...props
}) => {
  const submenu = useMenuData(menu);
  const { width } = useWindowSize();
  const desktopSize = 992;
  const mobileMenu = width < desktopSize;
  const isTabbedMenu = submenu.some(menuItem => menuItem.businessType);

  return (
    <StyledContainer
      as={mobileMenu ? 'div' : menu?.url ? 'a' : 'div'}
      className="menu-item-container"
      isOpen={isOpen}
      isTabbedMenu={isTabbedMenu}
      {...(menu?.url && { href: parseUrl(menu?.url)?.to })}
      {...props}
    >
      <div className="menu-item-heading" onClick={onClick} onMouseEnter={onMouseEnter} role="presentation">
        <Text
          fontSize="textXs"
          fontWeight="semiBold"
          textColor="gray-900"
          display="block"
          cursor="pointer"
          width="100%"
          paddingY={16}
          textTransform="none"
          textAlign="left"
          transition="color 400ms ease-in-out"
          md={{ width: 'fit-content' }}
          lg={{ paddingAll: 0, textColor: isOpen ? 'green-300' : 'common-white' }}
        >
          {menu?.heading}
        </Text>
        <Flex transition="200ms ease-in-out" transform={isOpen ? 'rotate(180deg)' : 'rotate(0)'}>
          <Icon
            id="chevron-down"
            fill={
              isOpen
                ? `${mobileMenu ? color.gray[900] : color.green[300]}`
                : `${mobileMenu ? color.gray[900] : color.common.white}`
            }
            size={18}
          />
        </Flex>
      </div>
      <Container
        cursor="default"
        onMouseLeave={() => setOpenMenu(-1)}
        className={`dropdown ${menu?.heading?.toLowerCase().replace(' ', '-')}-dropdown`}
      >
        <Flex
          className={`header-menu-arrow ${menu?.heading?.toLowerCase().replace(' ', '-')}-arrow`}
          display={isOpen ? 'block' : 'none'}
        />
        <Flex
          paddingLeft={isTabbedMenu ? 0 : 24}
          paddingRight={isTabbedMenu ? 0 : 16}
          paddingTop={isTabbedMenu ? 0 : 24}
          paddingBottom={isTabbedMenu ? 0 : 8}
          backgroundColor="common-white"
          width="max-content"
          display={isOpen ? 'flex' : 'none'}
          lg={{ boxShadow: 'xl' }}
          flexWrap="wrap"
          gap="64px"
        >
          {isTabbedMenu ? (
            <TabbedSubMenu menu={submenu} setOpenMenu={setOpenMenu} />
          ) : (
            submenu.map((subMenuItem, index) =>
              subMenuItem.groups.map(group => (
                <SubMenuGroup
                  group={group}
                  mobileMenu={mobileMenu}
                  isOpen={isOpen}
                  handleMobileMenu={handleMobileMenu}
                  key={`submenu-group-${index}`}
                  setOpenMenu={setOpenMenu}
                />
              )),
            )
          )}
        </Flex>
      </Container>
    </StyledContainer>
  );
};

export default MenuItem;

const StyledContainer = styled.div`
  text-decoration: none;

  .menu-item-container {
    height: fit-content;
    cursor: pointer;
  }

  .menu-item-heading {
    align-items: center;
    display: flex;
    gap: 5px;
    height: fit-content;
    justify-content: space-between;
    padding: 0 24px;
    position: relative;
  }

  .dropdown {
    position:relative;
    width: 100%;
    max-width: 100%;
    top: 0;
    left: 0%;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    max-height ${({ isOpen }) => (isOpen ? 'fit-content' : '0')};
    transition: max-height .3s ease-in-out, opacity .3s ease-in-out;
    background-color: common-white;

    .header-menu-arrow {
      width: 100%;
      position: absolute;
      height: 15px;
      top: -15px;
      left: 0px;
      transition: none;

      &::before {
        border-bottom: 11px solid white;
        border-left: 11px solid transparent;
        border-right: 11px solid transparent;
        content: '';
        display: inline-block;
        left: 0px;
        margin-left: auto;
        margin-right: auto;
        position: absolute;
        right: 0px;
        top: 5px;
        width: 1px;
      }

      &.products-arrow {
        left: 180px;
      }

      &.industries-arrow {
        left: 50px;
      }

      &.resources-arrow {
        left: 120px;
      }

      &.company-arrow {
        left: 115px;
      }

      &.support-arrow {
        left: 70px;
      }
    }

    &.industries-dropdown {
      left: -10px;
    }
    &.resources-dropdown {
      left: -80px;
    }
    &.company-dropdown {
      left: -80px;
    }
    &.support-dropdown {
      left: -40px;
    }
  }

  @media (min-width: 992px) {
    .menu-item-heading {
      height: 100%;
      padding: 0;
      border-bottom: none;
    }

    .dropdown {
      position: absolute;
      top: 60px;
      left:${({ isTabbedMenu }) => (isTabbedMenu ? '-150px ' : '0px')}; 
      max-width: 900px;
      transform: translateX(-50%);
      height: fit-content;
      background-color: transparent
    }
  }


`;
